/** The contests a temporary account may open: its own, and its warm-up when it has one. */
export function tempContestSlugs(hint: { slug: string; warmUp?: { slug: string } | null }): string[] {
  return hint.warmUp?.slug ? [hint.slug, hint.warmUp.slug] : [hint.slug];
}
export function tempPageAllowed(pathname: string, locale: string, slugs: string | readonly string[]): boolean {
  const path = pathname.replace(/\/$/, "");
  const inContest = (typeof slugs === "string" ? [slugs] : slugs).some((slug) => {
    const contest = `/${locale}/contests/${slug}`;
    return path === contest || path.startsWith(`${contest}/`);
  });
  return inContest
    || path === `/${locale}/playground` || path.startsWith(`/${locale}/playground/`)
    || path === `/${locale}/dashboard/profile`;
}

/**
 * Where `ContestTempBoundary` must keep rendering a skeleton until the
 * viewer's identity is resolved, and where it must not (2026-09-14, founder).
 *
 * The boundary used to hold the whole tree behind `useAuth().isLoading`, and
 * that flag is derived from a client-side bootstrap, so it is always true on
 * the server: every locale route server-rendered the skeleton, and the site
 * shipped 7 KB of `aria-busy` shell to any client that does not run scripts.
 * Its invariant is real, though: "no child client component mounts before the
 * identity is resolved", so a contest temporary account's token never fires
 * the reads of a surface it must not see. Those reads live on the account's
 * own surfaces, listed here, and nowhere a stranger can already browse.
 *
 * Decided from the path, which the server and the client agree on, so the
 * first client render matches the server one and nothing hydrates twice.
 * Public pages (marketing, courses, judge, contests, playground, the quiz
 * catalogue, profiles) render on the server again; a temporary session that
 * lands on one is still sent to its contest, by the middleware first and the
 * boundary's effect second, it just sees the public page instead of a blank
 * while that happens.
 */
const IDENTITY_GATED_SEGMENTS: ReadonlySet<string> = new Set([
  "dashboard",
  "assignments",
  "checkout",
  "order-success",
  "payment-link",
  "quiz",
]);

export function identityGatedRoute(pathname: string, locale: string): boolean {
  const segments = pathname.split("/").filter(Boolean);
  const first = segments[0] === locale ? segments[1] : segments[0];
  if (first === undefined) return false;
  if (IDENTITY_GATED_SEGMENTS.has(first)) return true;
  if (isHireWorkspacePath(segments, locale)) return true;
  // The quiz attempt and results shell, under the otherwise public catalogue.
  return first === "quiz-exams" && (segments.includes("attempt") || segments.includes("results"));
}

/**
 * The gated routes whose shell holds the identity itself (`AD-DSH02`): the
 * dashboard. Its frame (menu, top bar) reads nothing an account owns, so it is
 * rendered on the server and paints with the page; `DashboardShell` keeps its
 * content and its own reads (notifications, announcements, live classes)
 * behind the resolved identity, which is the boundary's invariant kept one
 * level down. Until then the whole dashboard was a grey box until the session
 * check, the profile and the page's reads had all answered.
 */
export function shellHoldsIdentity(pathname: string, locale: string): boolean {
  const segments = pathname.split("/").filter(Boolean);
  const first = segments[0] === locale ? segments[1] : segments[0];
  return first === "dashboard" || isHireWorkspacePath(segments, locale);
}

/**
 * `HR-ORG01b`: the Hire company workspace, `/[locale]/hire/app/...`, is an
 * account's own surface with a shell of its own that holds the identity the
 * dashboard's way (`HireShell`). `/[locale]/hire` itself is the public
 * landing (`HR-WEB01`) and stays public.
 */
function isHireWorkspacePath(segments: readonly string[], locale: string): boolean {
  const rest = segments[0] === locale ? segments.slice(1) : segments;
  return rest[0] === "hire" && rest[1] === "app";
}
